import React, { Fragment } from 'react';
import { AsYouType, parsePhoneNumber } from 'libphonenumber-js';
import HtmlInput from '../html/Input';
import InputError from '../errors/InputError';

const isModifierKey = event => {
  const key = event.keyCode;
  return (
    event.shiftKey === true ||
    key === 35 ||
    key === 36 || // Allow Shift, Home, End
    key === 9 ||
    key === 13 ||
    key === 46 ||
    key === 188 || // Allow Backspace (key=8), Tab, Enter, Delete, ','
    (key > 36 && key < 41) || // Allow left, up, right, down
    // Allow Ctrl/Command + A,C,V,X,Z
    ((event.ctrlKey === true || event.metaKey === true) &&
      (key === 65 || key === 67 || key === 86 || key === 88 || key === 90 || key === 91))
  );
};

export const processPhoneNumbersStringCaretaker = (notificationNumbers, caretakerNames) => {
  const phoneNumbers = notificationNumbers.split(',');
  const caretakers = caretakerNames ? caretakerNames.split(',') : [];

  return phoneNumbers.map((number, index) => {
    const caretakerName = caretakers[index]?.trim() || '';
    try {
      const parsed = parsePhoneNumber(number, 'US');
      return {
        ...parsed,
        caretakerName,
      };
    } catch (e) {
      return {
        number,
        nationalNumber: number,
        countryCallingCode: number === '+' ? '' : '1',
        caretakerName,
      };
    }
  });
};

export const formatPhoneNumberCaretaker = phoneNumbers =>
  phoneNumbers
    .map(({ nationalNumber, caretakerName }) => {
      const formattedNumber = new AsYouType('US').input(nationalNumber);
      return caretakerName ? `${formattedNumber} • ${caretakerName}` : formattedNumber;
    })
    .join(', ');

export const phoneNumberFormatPipeline = (numbers, names) => {
  const processedNumbers = processPhoneNumbersString(numbers, names);
  return formatPhoneNumber(processedNumbers);
};

export default class CaretakerPhoneInput extends React.Component {
  constructor(props) {
    super(props);
    this.myRef = React.createRef();
    this.state = {
      value: '',
      displayValue: props.value ? phoneNumberFormatPipeline(props.value, props.names || []) : undefined,
    };
  }

  processEvent = e => processPhoneNumbersString(e.target.value, this.props.names || []);

  handleChange = e => {
    const processedNumbers = this.processEvent(e);
    const nationalNumbers = processedNumbers.map(n => n.nationalNumber).join(',');
    const plainNumbers = processedNumbers.map(n => n.plainNumber).join(',');
    this.setState({ displayValue: formatPhoneNumber(processedNumbers) });

    this.props.onChange(plainNumbers);
  };

  handleKeyDown = e => {
    if (
      e.keyCode === 8 &&
      e.target.selectionStart !== e.target.value.length &&
      e.target.selectionStart === e.target.selectionEnd
    ) {
      e.preventDefault();
    }
  };

  handleOnKeyUp = e => {
    if (e.target.selectionStart !== e.target.selectionEnd || isModifierKey(e)) {
      return;
    } else if (e.keyCode === 8 && e.target.selectionStart !== e.target.value.length) {
      e.target.setSelectionRange(e.target.selectionStart - 1, e.target.selectionStart);
      return;
    }

    const { nationalNumbers } = this.processEvent(e);
    const generatedOutput = formatPhoneNumber(nationalNumbers);
    const currentCursorPosition = e.target.selectionStart;
    let finalCursorPosition = currentCursorPosition;

    if (currentCursorPosition === nationalNumbers.length) {
      finalCursorPosition = generatedOutput.length;
    } else if (currentCursorPosition === nationalNumbers.lastIndexOf(',') + 2) {
      finalCursorPosition = finalCursorPosition + 1;
    }

    e.target.selectionStart = finalCursorPosition;
    e.target.selectionEnd = finalCursorPosition;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({
        displayValue: this.props.value ? phoneNumberFormatPipeline(this.props.value) : '',
      });
    }
  }

  render() {
    const {
      Input = HtmlInput,
      Error = InputError,
      placeholder = '',
      isHidden,
      autoFocus,
      value,
      isDisabled,
      name = 'text',
      error,
      hasError,
      onKeyDown, // do not delete because of ...rest
      onChange, // do not delete because of ...rest
      ...rest
    } = this.props;
    return (
      <Fragment>
        <Input
          ref={this.myRef}
          id={name}
          type={isHidden ? 'hidden' : 'text'}
          name={name}
          autoComplete="on"
          autoCapitalize="off"
          autoCorrect="off"
          placeholder={placeholder}
          autoFocus={autoFocus || false}
          value={this.state.displayValue || ''}
          onChange={this.handleChange}
          onKeyUp={this.handleOnKeyUp}
          onKeyDown={this.handleKeyDown}
          disabled={isDisabled}
          hasError={hasError}
          {...rest}
        />
        <Error isVisible={hasError} id={name}>
          {hasError ? error : ''}
        </Error>
      </Fragment>
    );
  }
}
